import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  message, 
  Modal, 
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Statistic,
  Collapse,
  Select,
  Dropdown,
  Menu,
  DatePicker,
  Tabs
} from 'antd';
import { 
  LogoutOutlined,
  FileTextOutlined,
  CheckOutlined,
  CloseOutlined,
  UserOutlined,
  TeamOutlined,
  FilterOutlined,
  DownloadOutlined,
  DownOutlined,
  CalendarOutlined,
  ReloadOutlined,
  MedicineBoxOutlined,
  EyeOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { responsibleAuthService } from '../services/responsibleAuthService';
import axios from '../utils/axios';
import dayjs from 'dayjs';

// 安全的金额格式化函数
const formatAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return '0.00';
  }
  return amount.toFixed(2);
};

const { TextArea } = Input;
const { Panel } = Collapse;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// 志愿者分组数据接口
interface VolunteerGroup {
  volunteer_id: number;
  volunteer_name: string;
  volunteer_phone: string;
  responsible_name: string;
  responsible_phone: string;
  labor_details: LaborDetail[];
  stats: {
    total: number;
    volunteer_pending: number;
    responsible_pending: number;
    admin_pending: number;
    admin_settled: number;
    paid: number;
    totalAmount: number;
    confirmedAmount: number;
  };
}

interface LaborDetail {
  id: number;
  doctor_id: number;
  project_id: number;
  volunteer_id: number;
  survey_count: number;
  original_amount: number;
  after_tax_amount: number;
  before_tax_amount: number;
  tax_amount: number;
  remark?: string;
  createdAt: string;
  
  // 多级审批字段
  volunteer_status: 'pending' | 'confirmed' | 'rejected';
  volunteer_confirmed_at?: string;
  volunteer_confirmed_count?: number;
  volunteer_confirmed_amount?: number;
  volunteer_remark?: string;
  
  responsible_status: 'pending' | 'confirmed' | 'rejected';
  responsible_confirmed_at?: string;
  responsible_reject_reason?: string;
  
  overall_status: 'volunteer_pending' | 'responsible_pending' | 'admin_pending' | 'admin_settled' | 'paid' | 'rejected';
  project_type: 'survey' | 'ambassador';
  
  doctor?: {
    id: number;
    name: string;
    phone: string;
    hospitalName: string;
    departmentName: string;
    volunteers?: Array<{
      id: number;
      name: string;
      phone: string;
    }>;
  };
  project?: {
    id: number;
    name: string;
    project_type: 'survey' | 'ambassador';
  };
  volunteer?: {
    id: number;
    name: string;
    phone: string;
    responsible_person_name: string;
    responsible_person_phone: string;
  };
  
  // 权限信息
  user_permissions?: {
    can_volunteer_operate: boolean;
    can_responsible_operate: boolean;
    is_own_record: boolean;
  };
}

// 医生信息接口
interface DoctorInfo {
  binding_id: number;
  doctor: {
    id: number;
    name: string;
    phone: string;
    hospitalName: string;
    departmentName: string;
    created_at: string;
    phoneBindingStatus: boolean;
  };
  volunteer: {
    id: number;
    name: string;
    phone: string;
    responsible_person_name: string;
    responsible_person_phone: string;
  };
  project: {
    id: number;
    name: string;
    project_type: string;
  } | null;
  binding_info: {
    status: string;
    created_at: string;
    doctor_code: string;
    hospital_code: string;
  };
  submission_stats: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    confirmed: number;
    settled: number;
    paid: number;
  };
  latest_submission_time: string | null;
}

// 医生问卷提交记录接口
interface DoctorSubmission {
  id: number;
  survey: {
    id: number;
    title: string;
    description: string;
    type: string;
  };
  project: {
    id: number;
    name: string;
    project_type: string;
  };
  doctor: {
    id: number;
    name: string;
    phone: string;
    hospitalName: string;
    departmentName: string;
  };
  volunteer: {
    id: number;
    name: string;
    phone: string;
  } | null;
  status: string;
  submission_type: string;
  wechat_nickname: string;
  approved_by: number;
  approved_at: string;
  approval_remark: string;
  created_at: string;
}

const ResponsibleProject: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<LaborDetail[]>([]);
  const [groupedData, setGroupedData] = useState<VolunteerGroup[]>([]);
  const [viewMode, setViewMode] = useState<'grouped' | 'detailed'>('detailed');
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [volunteerConfirmModalVisible, setVolunteerConfirmModalVisible] = useState(false);
  const [responsibleConfirmModalVisible, setResponsibleConfirmModalVisible] = useState(false);
  const [batchResponsibleConfirmModalVisible, setBatchResponsibleConfirmModalVisible] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<LaborDetail | null>(null);
  const [selectedVolunteerGroup, setSelectedVolunteerGroup] = useState<VolunteerGroup | null>(null);
  const [volunteerForm] = Form.useForm();
  const [responsibleForm] = Form.useForm();
  const [batchResponsibleForm] = Form.useForm();
  const [stats, setStats] = useState({
    total: 0,
    volunteer_pending: 0,
    responsible_pending: 0,
    admin_pending: 0,
    admin_settled: 0,
    paid: 0,
    totalAmount: 0,
    confirmedAmount: 0
  });
  const [userInfo, setUserInfo] = useState<any>(null);
  
  // 分页和筛选状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [volunteerStatusFilter, setVolunteerStatusFilter] = useState<string>('all');
  const [responsibleStatusFilter, setResponsibleStatusFilter] = useState<string>('all');
  const [userInitialized, setUserInitialized] = useState(false);
  
  // 医生信息相关状态
  const [activeTab, setActiveTab] = useState<string>('labor');
  const [doctorData, setDoctorData] = useState<DoctorInfo[]>([]);
  const [doctorLoading, setDoctorLoading] = useState(false);
  const [doctorPagination, setDoctorPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [doctorStats, setDoctorStats] = useState({
    total_doctors: 0,
    active_bindings: 0,
    total_projects: 0
  });
  const [doctorFilters, setDoctorFilters] = useState({
    project_id: 'all',
    search_keyword: ''
  });
  const [projectList, setProjectList] = useState<any[]>([]);
  const [projectFilter, setProjectFilter] = useState<string>('all');
  
  // 医生问卷详情相关状态
  const [doctorSubmissionModalVisible, setDoctorSubmissionModalVisible] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);
  const [doctorSubmissions, setDoctorSubmissions] = useState<DoctorSubmission[]>([]);
  const [doctorSubmissionLoading, setDoctorSubmissionLoading] = useState(false);
  const [doctorSubmissionPagination, setDoctorSubmissionPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [doctorSubmissionStats, setDoctorSubmissionStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    confirmed: 0,
    settled: 0,
    paid: 0
  });

  // 获取当前登录的负责人信息
  const currentUser = responsibleAuthService.getUser();

  useEffect(() => {
    if (!responsibleAuthService.isAuthenticated()) {
      navigate('/responsible-login');
      return;
    }
    fetchData();
  }, []);

  // 获取项目列表
  const fetchProjects = async () => {
    try {
      const response = await axios.get('/responsible/projects');
      if (response.data.success) {
        setProjectList(response.data.data);
      }
    } catch (error) {
      console.error('获取项目列表失败:', error);
    }
  };

  useEffect(() => {
    if (userInfo) {
      fetchProjects();
    }
  }, [userInfo]);

  // 当筛选条件变化时重新获取数据
  useEffect(() => {
    if (userInfo) {
      fetchData(1); // 重置到第一页
    }
  }, [statusFilter, dateRange, volunteerStatusFilter, responsibleStatusFilter, projectFilter]);

  // 当医生筛选条件变化时重新获取医生数据
  useEffect(() => {
    if (activeTab === 'doctors' && userInfo) {
      fetchDoctorData(1);
    }
  }, [doctorFilters, activeTab]);

  // 根据用户身份设置默认视图模式（只在首次初始化时）
  useEffect(() => {
    if (userInfo?.permissions && !userInitialized) {
      let targetViewMode: 'grouped' | 'detailed';
      
      // 纯负责人身份默认使用分组视图，志愿者身份使用详细视图
      if (userInfo.permissions.is_responsible && !userInfo.permissions.is_volunteer) {
        targetViewMode = 'grouped';
      } else {
        targetViewMode = 'detailed';
      }
      
      // 如果目标视图模式和当前不同，需要重新获取数据
      if (targetViewMode !== viewMode) {
        setViewMode(targetViewMode);
        // 使用当前分页状态重新获取数据
        setTimeout(() => fetchData(1, 10, targetViewMode), 100);
      } else {
        setViewMode(targetViewMode);
      }
      
      setUserInitialized(true);
    }
  }, [userInfo, userInitialized]);

  // 获取劳务明细列表
  const fetchData = async (page?: number, pageSize?: number, targetViewMode?: 'grouped' | 'detailed') => {
    try {
      setLoading(true);
      
      const currentPage = page || pagination.current;
      const currentPageSize = pageSize || pagination.pageSize;
      const effectiveViewMode = targetViewMode || viewMode;
      
      // 判断是否为分组模式（纯负责人身份）
      // 如果userInfo还没加载，默认使用详细视图模式
      const useGroupPagination = userInfo?.permissions?.is_responsible && 
                                 !userInfo?.permissions?.is_volunteer && 
                                 effectiveViewMode === 'grouped';
      
      const params: any = {
        page: currentPage,
        pageSize: currentPageSize,
        status_filter: statusFilter,
        volunteer_group_pagination: useGroupPagination,
        project_id: projectFilter
      };

      // 添加时间筛选参数
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }

      // 添加志愿者状态筛选
      if (volunteerStatusFilter !== 'all') {
        params.volunteer_status = volunteerStatusFilter;
      }

      // 添加负责人状态筛选
      if (responsibleStatusFilter !== 'all') {
        params.responsible_status = responsibleStatusFilter;
      }

      const response = await axios.get('/responsible/labor-details', { params });
      
      if (response.data.success) {
        const responseData = response.data.data;
        
        // 更新分页信息
        setPagination({
          current: responseData.page,
          pageSize: responseData.pageSize,
          total: responseData.total
        });
        
        setStats(responseData.stats);
        setUserInfo(responseData.user_info);
        
        // 强制清空所有数据，然后重新设置
        setGroupedData([]);
        setData([]);
        
        // 短暂延迟后设置新数据，确保清空生效
        setTimeout(() => {
          if (responseData.grouped) {
            // 分组数据
            setGroupedData(responseData.list || []);
          } else {
            // 详细列表数据
            setData(responseData.list || []);
          }
        }, 10);
      } else {
        message.error('获取劳务明细失败');
      }
    } catch (error) {
      console.error('获取劳务明细失败:', error);
      message.error('获取劳务明细失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理分页变化
  const handlePaginationChange = (page: number, pageSize?: number) => {
    fetchData(page, pageSize);
  };

  // 获取医生信息数据
  const fetchDoctorData = async (page?: number, pageSize?: number) => {
    try {
      setDoctorLoading(true);
      
      const currentPage = page || doctorPagination.current;
      const currentPageSize = pageSize || doctorPagination.pageSize;
      
      const params: any = {
        page: currentPage,
        pageSize: currentPageSize,
        project_id: doctorFilters.project_id,
        search_keyword: doctorFilters.search_keyword
      };

      const response = await axios.get('/responsible/doctors', { params });
      
      if (response.data.success) {
        const responseData = response.data.data;
        
        setDoctorData(responseData.list || []);
        setDoctorPagination({
          current: responseData.page,
          pageSize: responseData.pageSize,
          total: responseData.total
        });
        setDoctorStats(responseData.stats);
      } else {
        message.error('获取医生信息失败');
      }
    } catch (error) {
      console.error('获取医生信息失败:', error);
      message.error('获取医生信息失败');
    } finally {
      setDoctorLoading(false);
    }
  };

  // 处理医生分页变化
  const handleDoctorPaginationChange = (page: number, pageSize?: number) => {
    fetchDoctorData(page, pageSize);
  };

  // 处理医生筛选条件变化
  const handleDoctorFilterChange = (key: string, value: string) => {
    setDoctorFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 查看医生问卷提交详情
  const viewDoctorSubmissions = async (doctorId: number) => {
    try {
      setSelectedDoctorId(doctorId);
      setDoctorSubmissionModalVisible(true);
      await fetchDoctorSubmissions(doctorId, 1);
    } catch (error) {
      console.error('获取医生问卷详情失败:', error);
      message.error('获取医生问卷详情失败');
    }
  };

  // 获取医生问卷提交记录
  const fetchDoctorSubmissions = async (doctorId: number, page?: number, pageSize?: number) => {
    try {
      setDoctorSubmissionLoading(true);
      
      const currentPage = page || doctorSubmissionPagination.current;
      const currentPageSize = pageSize || doctorSubmissionPagination.pageSize;
      
      const params: any = {
        page: currentPage,
        pageSize: currentPageSize
      };

      const response = await axios.get(`/responsible/doctors/${doctorId}/submissions`, { params });
      
      if (response.data.success) {
        const responseData = response.data.data;
        
        setDoctorSubmissions(responseData.list || []);
        setDoctorSubmissionPagination({
          current: responseData.page,
          pageSize: responseData.pageSize,
          total: responseData.total
        });
        setDoctorSubmissionStats(responseData.stats);
      } else {
        message.error('获取医生问卷详情失败');
      }
    } catch (error) {
      console.error('获取医生问卷详情失败:', error);
      message.error('获取医生问卷详情失败');
    } finally {
      setDoctorSubmissionLoading(false);
    }
  };

  // 处理医生问卷分页变化
  const handleDoctorSubmissionPaginationChange = (page: number, pageSize?: number) => {
    if (selectedDoctorId) {
      fetchDoctorSubmissions(selectedDoctorId, page, pageSize);
    }
  };

  // 处理视图模式变化
  const handleViewModeChange = (mode: 'grouped' | 'detailed') => {
    setViewMode(mode);
    // 直接传递新的视图模式给fetchData，避免状态更新时序问题
    fetchData(1, pagination.pageSize, mode);
  };

  // 处理状态筛选变化
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    // useEffect会自动触发数据刷新
  };

  // 处理项目筛选变化
  const handleProjectFilterChange = (value: string) => {
    setProjectFilter(value);
  };

  // 处理时间筛选变化
  const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setDateRange(dates);
  };

  // 处理志愿者状态筛选变化
  const handleVolunteerStatusFilterChange = (value: string) => {
    setVolunteerStatusFilter(value);
  };

  // 处理负责人状态筛选变化
  const handleResponsibleStatusFilterChange = (value: string) => {
    setResponsibleStatusFilter(value);
  };

  // 重置所有筛选条件
  const handleResetFilters = () => {
    setStatusFilter('all');
    setProjectFilter('all');
    setDateRange(null);
    setVolunteerStatusFilter('all');
    setResponsibleStatusFilter('all');
  };

  // 退出登录
  const handleLogout = () => {
    responsibleAuthService.logout();
    navigate('/responsible-login');
    message.success('已退出登录');
  };

  // 查看详情
  const viewDetail = async (record: LaborDetail) => {
    try {
      const response = await axios.get(`/labor-details/${record.id}`);
      if (response.data.success) {
        setSelectedDetail(response.data.data);
        setDetailModalVisible(true);
      } else {
        message.error('获取详情失败');
      }
    } catch (error) {
      console.error('获取详情失败:', error);
      message.error('获取详情失败');
    }
  };


  // 志愿者确认劳务明细
  const handleVolunteerConfirm = (record: LaborDetail) => {
    setSelectedDetail(record);
    volunteerForm.resetFields();
    
    // 根据项目类型设置默认值
    if (record.project_type === 'survey') {
      volunteerForm.setFieldsValue({
        confirmed_count: record.survey_count
      });
    } else if (record.project_type === 'ambassador') {
      volunteerForm.setFieldsValue({
        confirmed_amount: record.after_tax_amount
      });
    }
    
    setVolunteerConfirmModalVisible(true);
  };

  // 负责人确认劳务明细
  const handleResponsibleConfirm = (record: LaborDetail) => {
    setSelectedDetail(record);
    responsibleForm.resetFields();
    setResponsibleConfirmModalVisible(true);
  };

  // 批量负责人确认劳务明细
  const handleBatchResponsibleConfirm = (volunteerGroup: VolunteerGroup) => {
    setSelectedVolunteerGroup(volunteerGroup);
    batchResponsibleForm.resetFields();
    setBatchResponsibleConfirmModalVisible(true);
  };


  // 提交志愿者确认
  const submitVolunteerConfirm = async (values: any) => {
    if (!selectedDetail) return;
    
    try {
      const requestData: any = {
        remark: values.remark
      };
      
      // 根据项目类型添加相应参数
      if (selectedDetail.project_type === 'survey') {
        requestData.confirmed_count = values.confirmed_count;
      } else if (selectedDetail.project_type === 'ambassador') {
        requestData.confirmed_amount = values.confirmed_amount;
      }
      
      const response = await axios.post(`/responsible/labor-details/${selectedDetail.id}/volunteer-confirm`, requestData);
      
      if (response.data.success) {
        message.success(response.data.message);
        setVolunteerConfirmModalVisible(false);
        fetchData(); // 刷新数据
      } else {
        message.error(response.data.message || '确认失败');
      }
    } catch (error: any) {
      console.error('志愿者确认失败:', error);
      message.error(error.response?.data?.error || '志愿者确认失败');
    }
  };

  // 提交负责人确认/拒绝
  const submitResponsibleConfirm = async (values: any) => {
    if (!selectedDetail) return;
    
    try {
      const response = await axios.post(`/responsible/labor-details/${selectedDetail.id}/responsible-confirm`, {
        action: values.action,
        reason: values.reason
      });
      
      if (response.data.success) {
        message.success(response.data.message);
        setResponsibleConfirmModalVisible(false);
        fetchData(); // 刷新数据
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error: any) {
      console.error('负责人操作失败:', error);
      message.error(error.response?.data?.error || '负责人操作失败');
    }
  };

  // 提交批量负责人确认/拒绝
  const submitBatchResponsibleConfirm = async (values: any) => {
    if (!selectedVolunteerGroup) return;
    
    try {
      const response = await axios.post('/responsible/labor-details/batch-responsible-confirm', {
        volunteer_id: selectedVolunteerGroup.volunteer_id,
        action: values.action,
        reason: values.reason
      });
      
      if (response.data.success) {
        message.success(response.data.message);
        setBatchResponsibleConfirmModalVisible(false);
        fetchData(); // 刷新数据
      } else {
        message.error(response.data.message || '批量操作失败');
      }
    } catch (error: any) {
      console.error('批量负责人操作失败:', error);
      message.error(error.response?.data?.error || '批量负责人操作失败');
    }
  };

  // 导出Excel功能
  const exportToExcel = async (exportType: 'all' | 'current' | 'my_volunteer') => {
    try {
      const params: any = {
        status_filter: statusFilter,
        export_type: exportType,
        project_id: projectFilter
      };

      // 添加时间筛选参数
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }

      // 添加志愿者状态筛选
      if (volunteerStatusFilter !== 'all') {
        params.volunteer_status = volunteerStatusFilter;
      }

      // 添加负责人状态筛选
      if (responsibleStatusFilter !== 'all') {
        params.responsible_status = responsibleStatusFilter;
      }

      // 如果是分组模式，添加相应参数
      if (viewMode === 'grouped') {
        params.volunteer_group_pagination = userInfo?.permissions?.is_responsible && 
                                            !userInfo?.permissions?.is_volunteer;
      }

      // 根据导出类型设置不同的API端点
      let apiEndpoint = '/responsible/labor-details/export';
      if (exportType === 'my_volunteer') {
        apiEndpoint = '/responsible/labor-details/export/my-volunteer';
      }

      const response = await axios.get(apiEndpoint, {
        params,
        responseType: 'blob'
      });

      // 检查响应是否是错误（通过content-type判断）
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        message.error(errorData.message || '导出失败');
        return;
      }

      // 创建下载链接
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // 根据导出类型设置文件名
      let fileName = '劳务明细';
      if (exportType === 'current') {
        fileName += '_当前页面';
      } else if (exportType === 'my_volunteer') {
        fileName += '_我的志愿者';
      } else {
        fileName += '_全部数据';
      }
      fileName += `_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
      
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('数据导出成功');
    } catch (error: any) {
      console.error('导出失败:', error);
      
      // 处理blob响应中的错误信息
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          message.error(errorData.message || '导出失败');
        } catch {
          message.error('导出失败');
        }
      } else {
        message.error(error.response?.data?.message || '导出失败');
      }
    }
  };



  // 整体状态颜色映射
  const getOverallStatusColor = (status: string) => {
    switch (status) {
      case 'volunteer_pending': return 'orange';
      case 'responsible_pending': return 'blue';
      case 'admin_pending': return 'cyan';
      case 'admin_settled': return 'green';
      case 'paid': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  // 整体状态文本映射
  const getOverallStatusText = (status: string) => {
    switch (status) {
      case 'volunteer_pending': return '志愿者待确认';
      case 'responsible_pending': return '负责人待确认';
      case 'admin_pending': return '管理员待审核';
      case 'admin_settled': return '已审核待支付';
      case 'paid': return '已支付';
      case 'rejected': return '已拒绝';
      default: return '未知';
    }
  };

  // 志愿者状态颜色映射
  const getVolunteerStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'confirmed': return 'green';
      case 'rejected': return 'red';
      default: return 'default';
    }
  };

  // 志愿者状态文本映射
  const getVolunteerStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '待确认';
      case 'confirmed': return '已确认';
      case 'rejected': return '已拒绝';
      default: return '未知';
    }
  };

  // 负责人状态颜色映射
  const getResponsibleStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'confirmed': return 'green';
      case 'rejected': return 'red';
      default: return 'default';
    }
  };

  // 负责人状态文本映射
  const getResponsibleStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '待确认';
      case 'confirmed': return '已确认';
      case 'rejected': return '已拒绝';
      default: return '未知';
    }
  };

  // 问卷提交状态颜色映射
  const getSubmissionStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'approved': return 'green';
      case 'rejected': return 'red';
      case 'confirmed': return 'blue';
      case 'settled': return 'purple';
      case 'paid': return 'success';
      default: return 'default';
    }
  };

  // 问卷提交状态文本映射
  const getSubmissionStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '待审批';
      case 'approved': return '已审批';
      case 'rejected': return '已拒绝';
      case 'confirmed': return '已确认';
      case 'settled': return '已结算';
      case 'paid': return '已支付';
      default: return '未知';
    }
  };

  const columns: ColumnsType<LaborDetail> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '志愿者信息',
      key: 'volunteer',
      width: 120,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.volunteer?.name || '-'}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.volunteer?.phone || '-'}</div>
          {record.user_permissions?.is_own_record && (
            <Tag color="blue">本人</Tag>
          )}
        </div>
      ),
    },
    {
      title: '医生信息',
      key: 'doctor',
      width: 130,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.doctor?.name || '-'}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.doctor?.phone || '-'}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.doctor?.hospitalName || '-'}</div>
        </div>
      ),
    },
    {
      title: '项目信息',
      key: 'project',
      width: 120,
      render: (_, record) => (
        <div>
          <div>{record.project?.name || '-'}</div>
          <Tag  color={record.project_type === 'survey' ? 'green' : 'orange'}>
            {record.project_type === 'survey' ? '调研' : '大使'}
          </Tag>
        </div>
      ),
    },
    {
      title: '数量/金额',
      key: 'amounts',
      width: 120,
      render: (_, record) => (
        <div>
          <div>原始: {record.survey_count}份</div>
          <div>税后: ¥{formatAmount(record.original_amount)}</div>
          {record.volunteer_confirmed_count !== undefined && (
            <div style={{ color: 'green', fontSize: 12 }}>
              确认: {record.volunteer_confirmed_count}份
            </div>
          )}
          {record.volunteer_confirmed_amount !== undefined && record.volunteer_confirmed_amount !== null && (
            <div style={{ color: 'green', fontSize: 12 }}>
              确认: ¥{formatAmount(record.volunteer_confirmed_amount)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '志愿者状态',
      key: 'volunteer_status',
      width: 110,
      render: (_, record) => (
        <div>
          <Tag color={getVolunteerStatusColor(record.volunteer_status)}>
            {getVolunteerStatusText(record.volunteer_status)}
          </Tag>
          {record.volunteer_confirmed_at && (
            <div style={{ fontSize: 12, color: '#666' }}>
              {dayjs(record.volunteer_confirmed_at).format('MM-DD HH:mm')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '负责人状态',
      key: 'responsible_status',
      width: 110,
      render: (_, record) => (
        <div>
          <Tag color={getResponsibleStatusColor(record.responsible_status)}>
            {getResponsibleStatusText(record.responsible_status)}
          </Tag>
          {record.responsible_confirmed_at && (
            <div style={{ fontSize: 12, color: '#666' }}>
              {dayjs(record.responsible_confirmed_at).format('MM-DD HH:mm')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '整体状态',
      key: 'overall_status',
      width: 120,
      render: (_, record) => (
        <Tag color={getOverallStatusColor(record.overall_status)}>
          {getOverallStatusText(record.overall_status)}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => {
        const permissions = record.user_permissions;
        
        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => viewDetail(record)}
            >
              详情
            </Button>
            
            {/* 志愿者确认按钮 */}
            {permissions?.can_volunteer_operate && (
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleVolunteerConfirm(record)}
              >
                确认
              </Button>
            )}
            
            {/* 负责人确认/拒绝按钮 */}
            {permissions?.can_responsible_operate && (
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleResponsibleConfirm(record)}
              >
                审核
              </Button>
            )}
            

          </Space>
        );
      },
    },
  ];

  // 医生信息表格列配置
  const doctorColumns: ColumnsType<DoctorInfo> = [
    {
      title: '医生信息',
      key: 'doctor',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.doctor.name}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.doctor.phone}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.doctor.hospitalName}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.doctor.departmentName}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.doctor.phoneBindingStatus ? '已绑定' : '未绑定'}</div>
        </div>
      ),
    },
    {
      title: '绑定志愿者',
      key: 'volunteer',
      width: 120,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.volunteer.name}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.volunteer.phone}</div>
        </div>
      ),
    },
    {
      title: '项目信息',
      key: 'project',
      width: 120,
      render: (_, record) => (
        <div>
          {record.project ? (
            <>
              <div>{record.project.name}</div>
              <Tag color={record.project.project_type === 'survey' ? 'green' : 'orange'}>
                {record.project.project_type === 'survey' ? '调研' : '大使'}
              </Tag>
            </>
          ) : (
            <span style={{ color: '#999' }}>暂无项目</span>
          )}
        </div>
      ),
    },
    {
      title: '绑定状态',
      key: 'binding_status',
      width: 100,
      render: (_, record) => (
        <div>
          <Tag color={record.binding_info.status ? 'green' : 'red'}>
            {record.binding_info.status ? '活跃' : '非活跃'}
          </Tag>
          <div style={{ fontSize: 12, color: '#666' }}>
            {dayjs(record.binding_info.created_at).format('YYYY-MM-DD')}
          </div>
        </div>
      ),
    },
    {
      title: '问卷统计',
      key: 'submission_stats',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            <span>总计: </span>
            <Tag color="blue">{record.submission_stats.total}</Tag>
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {record.submission_stats.pending > 0 && (
              <Tag color="orange" style={{ fontSize: '12px' }}>待审批: {record.submission_stats.pending}</Tag>
            )}
            {record.submission_stats.approved > 0 && (
              <Tag color="green" style={{ fontSize: '12px' }}>已审批: {record.submission_stats.approved}</Tag>
            )}
            {record.submission_stats.paid > 0 && (
              <Tag color="purple" style={{ fontSize: '12px' }}>已支付: {record.submission_stats.paid}</Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '最近提交',
      key: 'latest_submission',
      width: 120,
      render: (_, record) => (
        <div>
          {record.latest_submission_time ? (
            <>
              <div style={{ fontSize: 12 }}>
                {dayjs(record.latest_submission_time).format('YYYY-MM-DD')}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {dayjs(record.latest_submission_time).format('HH:mm')}
              </div>
            </>
          ) : (
            <span style={{ color: '#999' }}>暂无提交</span>
          )}
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => viewDoctorSubmissions(record.doctor.id)}
          >
            查看问卷
          </Button>
        </Space>
      ),
    },
  ];

  // 医生问卷提交记录表格列配置
  const doctorSubmissionColumns: ColumnsType<DoctorSubmission> = [
    {
      title: '问卷信息',
      key: 'survey',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.survey.title}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.survey.description}</div>
          <Tag color={record.survey.type === 'research' ? 'blue' : 'green'}>
            {record.survey.type === 'research' ? '调研类' : '援助类'}
          </Tag>
        </div>
      ),
    },
    {
      title: '项目信息',
      key: 'project',
      width: 150,
      render: (_, record) => (
        <div>
          <div>{record.project.name}</div>
          <Tag color={record.project.project_type === 'survey' ? 'green' : 'orange'}>
            {record.project.project_type === 'survey' ? '调研' : '大使'}
          </Tag>
        </div>
      ),
    },
    {
      title: '提交状态',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tag color={getSubmissionStatusColor(record.status)}>
          {getSubmissionStatusText(record.status)}
        </Tag>
      ),
    },
    {
      title: '提交信息',
      key: 'submission_info',
      width: 120,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 12 }}>
            类型: {record.submission_type === 'doctor' ? '医生' : '匿名'}
          </div>
          {record.wechat_nickname && (
            <div style={{ fontSize: 12, color: '#666' }}>
              微信: {record.wechat_nickname}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '审批信息',
      key: 'approval_info',
      width: 150,
      render: (_, record) => (
        <div>
          {record.approved_at && (
            <>
              <div style={{ fontSize: 12 }}>
                {dayjs(record.approved_at).format('YYYY-MM-DD HH:mm')}
              </div>
              {record.approval_remark && (
                <div style={{ fontSize: 12, color: '#666' }}>
                  {record.approval_remark}
                </div>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 头部信息 */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <h2>负责人工作台</h2>
          <p>欢迎您，{currentUser?.name}</p>
        </Col>
        <Col>
          <Button 
            type="primary" 
            danger 
            icon={<LogoutOutlined />}
            onClick={handleLogout}
          >
            退出登录
          </Button>
        </Col>
      </Row>

      {/* 用户身份信息 */}
      {userInfo?.permissions && (
        <Row style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Card size="small">
              <Space>
                <span>当前身份：</span>
                {userInfo.permissions.is_volunteer && (
                  <Tag color="blue">志愿者</Tag>
                )}
                {userInfo.permissions.is_responsible && (
                  <Tag color="green">负责人</Tag>
                )}
                {userInfo.permissions.volunteer_name && (
                  <span>志愿者姓名：{userInfo.permissions.volunteer_name}</span>
                )}
                {userInfo.permissions.responsible_name && (
                  <span>负责人姓名：{userInfo.permissions.responsible_name}</span>
                )}
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {/* 统计信息 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card>
            <Statistic title="总计" value={stats.total} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic 
              title="志愿者待确认" 
              value={stats.volunteer_pending} 
              valueStyle={{ color: '#faad14' }} 
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic 
              title="负责人待确认" 
              value={stats.responsible_pending} 
              valueStyle={{ color: '#1890ff' }} 
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic 
              title="管理员待审核" 
              value={stats.admin_pending} 
              valueStyle={{ color: '#722ed1' }} 
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic 
              title="待支付" 
              value={stats.admin_settled} 
              valueStyle={{ color: '#52c41a' }} 
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic 
              title="已支付" 
              value={stats.paid} 
              valueStyle={{ color: '#13c2c2' }} 
            />
          </Card>
        </Col>
      </Row>

      {/* 金额统计 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card>
            <Statistic 
              title="总金额" 
              value={stats.totalAmount} 
              precision={2} 
              prefix="¥" 
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic 
              title="已确认金额" 
              value={stats.confirmedAmount} 
              precision={2} 
              prefix="¥" 
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 主要内容区域 - 使用Tabs */}
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        size="large"
        style={{ marginBottom: 16 }}
      >
        <TabPane 
          tab={
            <span>
              <FileTextOutlined />
              劳务明细管理
            </span>
          } 
          key="labor"
        >
          {/* 视图切换按钮 - 只有纯负责人身份才显示 */}
          {userInfo?.permissions?.is_responsible && !userInfo?.permissions?.is_volunteer && (
            <Row style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Card size="small">
                  <Space>
                    <span>显示模式：</span>
                    <Button
                      type={viewMode === 'grouped' ? 'primary' : 'default'}
                      icon={<TeamOutlined />}
                      onClick={() => handleViewModeChange('grouped')}
                    >
                      按志愿者分组
                    </Button>
                    <Button
                      type={viewMode === 'detailed' ? 'primary' : 'default'}
                      icon={<FileTextOutlined />}
                      onClick={() => handleViewModeChange('detailed')}
                    >
                      详细列表
                    </Button>
                  </Space>
                </Card>
              </Col>
            </Row>
          )}

          {/* 筛选条件和操作按钮 */}
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Card size="small" title={
                <Space>
                  <FilterOutlined style={{ color: '#1890ff' }} />
                  <span>筛选条件</span>
                </Space>
              }>
            <Row gutter={[16, 16]}>
              {/* 第一行：整体状态和时间筛选 */}
              <Col span={24}>
                <Row gutter={16} align="middle">
                  <Col>
                    <span>项目筛选：</span>
                  </Col>
                  <Col>
                    <Select
                      value={projectFilter}
                      onChange={handleProjectFilterChange}
                      style={{ width: 160 }}
                      placeholder="选择项目"
                    >
                      <Option value="all">全部项目</Option>
                      {projectList.map(project => (
                        <Option key={project.id} value={project.id}>{project.name}</Option>
                      ))}
                    </Select>
                  </Col>
                  <Col>
                    <span>整体状态：</span>
                  </Col>
                  <Col>
                    <Select
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      style={{ width: 160 }}
                      placeholder="选择状态"
                    >
                      <Option value="all">全部状态</Option>
                      <Option value="volunteer_pending">志愿者待确认</Option>
                      <Option value="responsible_pending">负责人待审核</Option>
                      <Option value="admin_pending">管理员待审核</Option>
                      <Option value="admin_settled">待支付</Option>
                      <Option value="paid">已支付</Option>
                      <Option value="rejected">已拒绝</Option>
                    </Select>
                  </Col>
                  <Col>
                    <CalendarOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                    <span>创建时间：</span>
                  </Col>
                  <Col>
                    <RangePicker
                      value={dateRange}
                      onChange={handleDateRangeChange}
                      style={{ width: 240 }}
                      placeholder={['开始日期', '结束日期']}
                      format="YYYY-MM-DD"
                    />
                  </Col>
                </Row>
              </Col>

              {/* 第二行：志愿者状态和负责人状态筛选 */}
              <Col span={24}>
                <Row gutter={16} align="middle">
                  <Col>
                    <span>志愿者状态：</span>
                  </Col>
                  <Col>
                    <Select
                      value={volunteerStatusFilter}
                      onChange={handleVolunteerStatusFilterChange}
                      style={{ width: 120 }}
                      placeholder="志愿者状态"
                    >
                      <Option value="all">全部</Option>
                      <Option value="pending">待确认</Option>
                      <Option value="confirmed">已确认</Option>
                      <Option value="rejected">已拒绝</Option>
                    </Select>
                  </Col>
                  <Col>
                    <span>负责人状态：</span>
                  </Col>
                  <Col>
                    <Select
                      value={responsibleStatusFilter}
                      onChange={handleResponsibleStatusFilterChange}
                      style={{ width: 120 }}
                      placeholder="负责人状态"
                    >
                      <Option value="all">全部</Option>
                      <Option value="pending">待确认</Option>
                      <Option value="confirmed">已确认</Option>
                      <Option value="rejected">已拒绝</Option>
                    </Select>
                  </Col>
                  <Col>
                    <Space>
                      <Button 
                        icon={<ReloadOutlined />}
                        onClick={handleResetFilters}
                        title="重置筛选条件"
                      >
                        重置
                      </Button>
                      <Dropdown
                        overlay={
                          <Menu
                            onClick={({ key }) => exportToExcel(key as 'all' | 'current' | 'my_volunteer')}
                            items={[
                              {
                                key: 'current',
                                icon: <FileTextOutlined />,
                                label: '导出当前页面数据',
                              },
                              {
                                key: 'all',
                                icon: <DownloadOutlined />,
                                label: '导出全部数据',
                              },
                                                        ...(userInfo?.permissions?.is_responsible && userInfo?.permissions?.is_volunteer ? [{
                            key: 'my_volunteer',
                            icon: <UserOutlined />,
                            label: '导出我负责的志愿者数据',
                          }] : [])
                            ]}
                          />
                        }
                        trigger={['click']}
                      >
                        <Button type="primary" icon={<DownloadOutlined />}>
                          导出数据 <DownOutlined />
                        </Button>
                      </Dropdown>
                    </Space>
                  </Col>
                </Row>
              </Col>

              {/* 筛选提示信息 */}
              <Col span={24}>
                <div style={{ fontSize: '12px', color: '#666', backgroundColor: '#f5f5f5', padding: '6px 12px', borderRadius: 4 }}>
                  <Space>
                    <span>当前视图：{viewMode === 'grouped' ? '按志愿者分组显示' : '详细列表显示'}</span>
                    <span>|</span>
                    <span>分页模式：{viewMode === 'grouped' ? '按志愿者分页' : '按记录分页'}</span>
                    {(dateRange || statusFilter !== 'all' || volunteerStatusFilter !== 'all' || responsibleStatusFilter !== 'all') && (
                      <>
                        <span>|</span>
                        <span style={{ color: '#1890ff' }}>已应用筛选条件</span>
                      </>
                    )}
                  </Space>
                </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

          {/* 数据表格显示 */}
          {viewMode === 'grouped' ? (
        <Card title="志愿者劳务明细分组">
          <Collapse
            expandIconPosition="right"
            size="large"
          >
            {groupedData.map(group => (
              <Panel
                key={group.volunteer_id}
                header={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                      <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                        {group.volunteer_name}
                      </span>
                      <span style={{ marginLeft: 8, color: '#666' }}>
                        ({group.volunteer_phone})
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <Space size="small">
                        <Tag color="blue">总计: {group.stats.total}</Tag>
                        {group.stats.responsible_pending > 0 && (
                          <Tag color="orange">待审核: {group.stats.responsible_pending}</Tag>
                        )}
                        <Tag color="green">¥{formatAmount(group.stats.totalAmount)}</Tag>
                      </Space>
                      {group.stats.responsible_pending > 0 && 
                       userInfo?.permissions?.is_responsible && 
                       !userInfo?.permissions?.is_volunteer && (
                        <Button
                          type="primary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBatchResponsibleConfirm(group);
                          }}
                        >
                          批量审核({group.stats.responsible_pending})
                        </Button>
                      )}
                    </div>
                  </div>
                }
              >
                <Table
                  columns={columns}
                  dataSource={group.labor_details}
                  rowKey="id"
                  pagination={{
                    pageSize: 5,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                    pageSizeOptions: ['5', '10', '20', '50'],
                    size: 'small'
                  }}
                  size="small"
                  scroll={{ x: 1200 }}
                />
                
                {/* 志愿者统计信息 */}
                <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f8f9fa', borderRadius: 4 }}>
                  <Row gutter={16}>
                    <Col span={4}>
                      <Statistic title="总记录" value={group.stats.total} />
                    </Col>
                    <Col span={4}>
                      <Statistic 
                        title="待志愿者确认" 
                        value={group.stats.volunteer_pending} 
                        valueStyle={{ color: '#faad14' }}
                      />
                    </Col>
                    <Col span={4}>
                      <Statistic 
                        title="待负责人审核" 
                        value={group.stats.responsible_pending} 
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Col>
                    <Col span={4}>
                      <Statistic 
                        title="待管理员审核" 
                        value={group.stats.admin_pending} 
                        valueStyle={{ color: '#722ed1' }}
                      />
                    </Col>
                    <Col span={4}>
                      <Statistic 
                        title="已支付" 
                        value={group.stats.paid} 
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Col>
                    <Col span={4}>
                      <Statistic 
                        title="总金额" 
                        value={group.stats.totalAmount} 
                        precision={2} 
                        prefix="¥"
                      />
                    </Col>
                  </Row>
                </div>
              </Panel>
            ))}
          </Collapse>
          
          {/* 分组视图分页 */}
          {pagination.total > 0 && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <div style={{ 
                display: 'inline-block', 
                padding: '8px 16px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666'
              }}>
                共 {pagination.total} 个志愿者，第 {pagination.current} / {Math.ceil(pagination.total / pagination.pageSize)} 页
              </div>
              <br />
              <Space style={{ marginTop: 8 }}>
                <Button 
                  disabled={pagination.current <= 1}
                  onClick={() => handlePaginationChange(pagination.current - 1)}
                >
                  上一页
                </Button>
                <Button 
                  disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                  onClick={() => handlePaginationChange(pagination.current + 1)}
                >
                  下一页
                </Button>
              </Space>
            </div>
          )}
        </Card>
      ) : (
        /* 详细列表显示 */
        <Card title="劳务明细列表">
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: handlePaginationChange,
              onShowSizeChange: handlePaginationChange
            }}
            scroll={{ x: 1200 }}
          />
          </Card>
          )}
        </TabPane>

        {/* 医生信息Tab */}
        <TabPane 
          tab={
            <span>
              <MedicineBoxOutlined />
              医生信息管理
            </span>
          } 
          key="doctors"
        >
          {/* 医生统计信息 */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card>
                <Statistic 
                  title="医生总数" 
                  value={doctorStats.total_doctors} 
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic 
                  title="活跃绑定" 
                  value={doctorStats.active_bindings} 
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic 
                  title="涉及项目" 
                  value={doctorStats.total_projects} 
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* 医生筛选条件 */}
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Card size="small" title={
                <Space>
                  <FilterOutlined style={{ color: '#1890ff' }} />
                  <span>医生筛选</span>
                </Space>
              }>
                <Row gutter={16} align="middle">
                  <Col>
                    <span>项目筛选：</span>
                  </Col>
                  <Col>
                    <Select
                      value={doctorFilters.project_id}
                      onChange={(value) => handleDoctorFilterChange('project_id', value)}
                      style={{ width: 160 }}
                      placeholder="选择项目"
                    >
                      <Option value="all">全部项目</Option>
                      {projectList.map(project => (
                        <Option key={project.id} value={project.id}>{project.name}</Option>
                      ))}
                    </Select>
                  </Col>
                  <Col>
                    <span>搜索关键词：</span>
                  </Col>
                  <Col>
                    <Input
                      value={doctorFilters.search_keyword}
                      onChange={(e) => handleDoctorFilterChange('search_keyword', e.target.value)}
                      placeholder="医生姓名/电话/医院"
                      style={{ width: 200 }}
                      prefix={<SearchOutlined />}
                      allowClear
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* 医生信息表格 */}
          <Card title="医生绑定信息">
            <Table
              columns={doctorColumns}
              dataSource={doctorData}
              rowKey="binding_id"
              loading={doctorLoading}
              pagination={{
                current: doctorPagination.current,
                pageSize: doctorPagination.pageSize,
                total: doctorPagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: handleDoctorPaginationChange,
                onShowSizeChange: handleDoctorPaginationChange
              }}
              scroll={{ x: 1000 }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* 详情弹窗 */}
      <Modal
        title="劳务明细详情"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedDetail(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDetailModalVisible(false);
            setSelectedDetail(null);
          }}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedDetail && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="劳务ID">{selectedDetail.id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={getOverallStatusColor(selectedDetail.overall_status)}>
                {getOverallStatusText(selectedDetail.overall_status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="医生姓名">{selectedDetail.doctor?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="医生电话">{selectedDetail.doctor?.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="医院名称">{selectedDetail.doctor?.hospitalName || '-'}</Descriptions.Item>
            <Descriptions.Item label="科室名称">{selectedDetail.doctor?.departmentName || '-'}</Descriptions.Item>
            <Descriptions.Item label="志愿者姓名">{selectedDetail.doctor?.volunteers?.[0]?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="志愿者电话">{selectedDetail.doctor?.volunteers?.[0]?.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="项目名称">{selectedDetail.project?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="问卷数量">{selectedDetail.survey_count} 份</Descriptions.Item>
            <Descriptions.Item label="税后金额">¥{formatAmount(selectedDetail.after_tax_amount)}</Descriptions.Item>
            <Descriptions.Item label="税前金额">¥{formatAmount(selectedDetail.before_tax_amount)}</Descriptions.Item>
            <Descriptions.Item label="整体状态">
              <Tag color={getOverallStatusColor(selectedDetail.overall_status)}>
                {getOverallStatusText(selectedDetail.overall_status)}
              </Tag>
            </Descriptions.Item>
            {selectedDetail.volunteer_status === 'confirmed' && (
              <>
                <Descriptions.Item label="志愿者确认问卷数量">
                  {selectedDetail.volunteer_confirmed_count || 0} 份
                </Descriptions.Item>
                <Descriptions.Item label="志愿者确认金额">
                  ¥{formatAmount(selectedDetail.volunteer_confirmed_amount)}
                </Descriptions.Item>
              </>
            )}
            {selectedDetail.remark && (
              <Descriptions.Item label="备注" span={2}>
                {selectedDetail.remark}
              </Descriptions.Item>
            )}
            
            {(selectedDetail as any).submission_details && (
              <Descriptions.Item label="问卷详情" span={2}>
                <div style={{ marginTop: 8 }}>
                  <p style={{ margin: '4px 0', fontSize: '12px', color: '#666' }}>
                    共 {(selectedDetail as any).submission_details.total_count} 份问卷，
                    已确认 {(selectedDetail as any).submission_details.confirmed_count} 份
                  </p>
                  {(selectedDetail as any).submission_details.submissions && 
                   (selectedDetail as any).submission_details.submissions.length > 0 && (
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
                      {(selectedDetail as any).submission_details.submissions.map((sub: any, index: number) => {
                        // 根据问卷状态显示不同的图标和文字
                        const getSubmissionStatusIcon = (status: string) => {
                          switch (status) {
                            case 'approved': return '⏳'; // 已审批
                            case 'confirmed': return '✅'; // 已确认
                            case 'settled': return '💰'; // 已结算
                            case 'paid': return '💳'; // 已支付
                            default: return '⭕'; // 其他状态
                          }
                        };
                        
                        const getSubmissionStatusText = (status: string) => {
                          switch (status) {
                            case 'approved': return '已审批';
                            case 'confirmed': return '已确认';
                            case 'settled': return '已结算';
                            case 'paid': return '已支付';
                            default: return '未知状态';
                          }
                        };
                        
                        const getSubmissionStatusColor = (status: string) => {
                          switch (status) {
                            case 'approved': return '#faad14';
                            case 'confirmed': return '#52c41a';
                            case 'settled': return '#1890ff';
                            case 'paid': return '#722ed1';
                            default: return '#d9d9d9';
                          }
                        };
                        
                        return (
                          <div key={sub.id} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            padding: '4px 0',
                            borderBottom: index < (selectedDetail as any).submission_details.submissions.length - 1 ? '1px solid #f0f0f0' : 'none'
                          }}>
                            <span style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                              <span style={{ marginRight: 8 }}>{getSubmissionStatusIcon(sub.status)}</span>
                              <span>{sub.title}</span>
                                                             <Tag 
                                 color={getSubmissionStatusColor(sub.status)}
                                 style={{ marginLeft: 8, fontSize: '10px' }}
                               >
                                {getSubmissionStatusText(sub.status)}
                              </Tag>
                            </span>
                            <span style={{ marginLeft: 8, color: '#666', fontSize: '12px' }}>
                              ¥{sub.price}
                            </span>
                            <span style={{ marginLeft: 8, color: '#999', fontSize: '12px' }}>
                              {new Date(sub.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>


      {/* 志愿者确认弹窗 */}
      <Modal
        title="志愿者确认"
        open={volunteerConfirmModalVisible}
        onCancel={() => {
          setVolunteerConfirmModalVisible(false);
          setSelectedDetail(null);
        }}
        onOk={() => volunteerForm.submit()}
        okText="确认"
        cancelText="取消"
        width={600}
      >
        <Form
          form={volunteerForm}
          onFinish={submitVolunteerConfirm}
          layout="vertical"
        >
          {selectedDetail && (
            <>
              <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <p><strong>项目类型：</strong>
                  <Tag color={selectedDetail.project_type === 'survey' ? 'green' : 'orange'}>
                    {selectedDetail.project_type === 'survey' ? '调研项目' : '大使项目'}
                  </Tag>
                </p>
                <p><strong>原始问卷数量：</strong>{selectedDetail.survey_count} 份</p>
                <p style={{ margin: 0 }}><strong>原始金额：</strong>¥{formatAmount(selectedDetail.after_tax_amount)}</p>
              </div>
              
              {selectedDetail.project_type === 'survey' ? (
                <Form.Item 
                  label="确认问卷数量" 
                  name="confirmed_count"
                  rules={[
                    { required: true, message: '请输入确认数量' },
                    { type: 'number', min: 0, max: selectedDetail.survey_count, message: `数量范围：0-${selectedDetail.survey_count}` }
                  ]}
                >
                  <InputNumber 
                    min={0} 
                    max={selectedDetail.survey_count}
                    style={{ width: '100%' }}
                    placeholder="请输入确认的问卷数量"
                  />
                </Form.Item>
              ) : (
                <Form.Item 
                  label="确认金额" 
                  name="confirmed_amount"
                  rules={[
                    { required: true, message: '请输入确认金额' },
                    { type: 'number', min: 0, message: '金额必须大于等于0' }
                  ]}
                >
                  <InputNumber 
                    min={0}
                    precision={2}
                    style={{ width: '100%' }}
                    placeholder="请输入确认的金额"
                    addonBefore="¥"
                  />
                </Form.Item>
              )}
            </>
          )}
          
          <Form.Item label="备注" name="remark">
            <TextArea 
              rows={3} 
              placeholder="请输入确认备注（可选）"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 负责人确认弹窗 */}
      <Modal
        title="负责人审核"
        open={responsibleConfirmModalVisible}
        onCancel={() => {
          setResponsibleConfirmModalVisible(false);
          setSelectedDetail(null);
          responsibleForm.resetFields();
        }}
        footer={null}  // 移除默认按钮，避免混乱
        width={600}
      >
        <Form
          form={responsibleForm}
          onFinish={submitResponsibleConfirm}
          layout="vertical"
        >
          {selectedDetail && (
            <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
              <p><strong>志愿者：</strong>{selectedDetail.volunteer?.name}</p>
              <p><strong>医生：</strong>{selectedDetail.doctor?.name}</p>
              <p><strong>项目：</strong>{selectedDetail.project?.name}</p>
              <p><strong>志愿者确认数量：</strong>{selectedDetail.volunteer_confirmed_count || 0} 份</p>
              <p style={{ margin: 0 }}><strong>志愿者确认金额：</strong>¥{formatAmount(selectedDetail.volunteer_confirmed_amount)}</p>
              {selectedDetail.volunteer_remark && (
                <p style={{ margin: 0 }}><strong>志愿者备注：</strong>{selectedDetail.volunteer_remark}</p>
              )}
            </div>
              
          )}
          
          <Form.Item 
            label="原因/备注" 
            name="reason"
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  if (getFieldValue('action') === 'reject' && !value) {
                    return Promise.reject(new Error('拒绝时必须填写原因'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <TextArea 
              rows={3} 
              placeholder="请输入审核原因或备注"
            />
          </Form.Item>
          <Form.Item 
                label="审核结果" 
                name="action"
                rules={[{ required: true, message: '请选择审核结果' }]}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button 
                    type="primary" 
                    block 
                    icon={<CheckOutlined />}
                    onClick={() => {
                      responsibleForm.setFieldsValue({ action: 'confirm' });
                      responsibleForm.submit();
                    }}
                  >
                    确认通过
                  </Button>
                  <Button 
                    danger 
                    block 
                    icon={<CloseOutlined />}
                    onClick={() => {
                      responsibleForm.setFieldsValue({ action: 'reject' });
                      responsibleForm.submit();
                    }}
                  >
                    拒绝
                  </Button>
                </Space>
              </Form.Item>

        </Form>
      </Modal>

      {/* 批量负责人确认弹窗 */}
      <Modal
        title="批量负责人审核"
        open={batchResponsibleConfirmModalVisible}
        onCancel={() => {
          setBatchResponsibleConfirmModalVisible(false);
          setSelectedVolunteerGroup(null);
          batchResponsibleForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={batchResponsibleForm}
          onFinish={submitBatchResponsibleConfirm}
          layout="vertical"
        >
          {selectedVolunteerGroup && (
            <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
              <p><strong>志愿者：</strong>{selectedVolunteerGroup.volunteer_name}</p>
              <p><strong>电话：</strong>{selectedVolunteerGroup.volunteer_phone}</p>
              <p><strong>待审核劳务明细：</strong>{selectedVolunteerGroup.stats.responsible_pending} 条</p>
              <p style={{ margin: 0 }}><strong>涉及总金额：</strong>¥{formatAmount(selectedVolunteerGroup.stats.totalAmount)}</p>
              <div style={{ marginTop: 8 }}>
                <strong>包含医生：</strong>
                {selectedVolunteerGroup.labor_details
                  .filter(detail => detail.overall_status === 'responsible_pending')
                  .map(detail => detail.doctor?.name)
                  .filter((name, index, arr) => arr.indexOf(name) === index)
                  .join('、')
                }
              </div>
            </div>
          )}
          
          <Form.Item 
            label="审核意见/拒绝原因" 
            name="reason"
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  if (getFieldValue('action') === 'reject' && !value) {
                    return Promise.reject(new Error('批量拒绝时必须填写原因'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <TextArea 
              rows={4} 
              placeholder="请输入审核意见或拒绝原因"
            />
          </Form.Item>
          
          <Form.Item 
            label="批量操作" 
            name="action"
            rules={[{ required: true, message: '请选择操作类型' }]}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary" 
                block 
                size="large"
                icon={<CheckOutlined />}
                onClick={() => {
                  batchResponsibleForm.setFieldsValue({ action: 'confirm' });
                  batchResponsibleForm.submit();
                }}
              >
                批量确认通过 ({selectedVolunteerGroup?.stats.responsible_pending || 0} 条)
              </Button>
              <Button 
                danger 
                block 
                size="large"
                icon={<CloseOutlined />}
                onClick={() => {
                  batchResponsibleForm.setFieldsValue({ action: 'reject' });
                  batchResponsibleForm.submit();
                }}
              >
                批量拒绝 ({selectedVolunteerGroup?.stats.responsible_pending || 0} 条)
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 医生问卷详情弹窗 */}
      <Modal
        title={`医生问卷提交详情${selectedDoctorId ? ` - 医生ID: ${selectedDoctorId}` : ''}`}
        open={doctorSubmissionModalVisible}
        onCancel={() => {
          setDoctorSubmissionModalVisible(false);
          setSelectedDoctorId(null);
          setDoctorSubmissions([]);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDoctorSubmissionModalVisible(false);
            setSelectedDoctorId(null);
            setDoctorSubmissions([]);
          }}>
            关闭
          </Button>
        ]}
        width={1200}
      >
        {/* 医生问卷统计 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Statistic title="总计" value={doctorSubmissionStats.total} />
          </Col>
          <Col span={4}>
            <Statistic 
              title="待审批" 
              value={doctorSubmissionStats.pending} 
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="已审批" 
              value={doctorSubmissionStats.approved} 
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="已拒绝" 
              value={doctorSubmissionStats.rejected} 
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="已确认" 
              value={doctorSubmissionStats.confirmed} 
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="已支付" 
              value={doctorSubmissionStats.paid} 
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
        </Row>

        {/* 医生问卷提交记录表格 */}
        <Table
          columns={doctorSubmissionColumns}
          dataSource={doctorSubmissions}
          rowKey="id"
          loading={doctorSubmissionLoading}
          pagination={{
            current: doctorSubmissionPagination.current,
            pageSize: doctorSubmissionPagination.pageSize,
            total: doctorSubmissionPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            pageSizeOptions: ['5', '10', '20', '50'],
            onChange: handleDoctorSubmissionPaginationChange,
            onShowSizeChange: handleDoctorSubmissionPaginationChange
          }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </Modal>
    </div>
  );
};

export default ResponsibleProject; 